import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Clock, Sparkles, Banknote, QrCode, Loader2, CheckCircle2 } from "lucide-react";
import { announceBooking } from "@/components/FirstVisitLocation";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { PaymentDialog } from "@/components/PaymentDialog";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const niceDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("en", { weekday: "long", month: "long", day: "numeric" });

function BookingForm({ e, onBooked }) {
  const [kids, setKids] = useState([]);
  const [pick, setPick] = useState([]);
  const [arrival, setArrival] = useState("");
  const [pay, setPay] = useState("cash");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/children").then((r) => setKids(r.data)).catch(() => {}); }, []);
  const toggle = (id) => setPick(pick.includes(id) ? pick.filter((x) => x !== id) : [...pick, id]);
  const book = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/events/${e.id}/book`, { child_ids: pick, arrival, payment_method: pay }); onBooked(data, pay); setPick([]); }
    catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  if (!kids.length) { return <p className="text-sm text-slate-600">Add your Explorer in <Link to="/dashboard/explorers" className="underline font-bold">My Explorers</Link> first, then come back to book.</p>; }
  return (
    <div className="space-y-4">
      <div><p className="font-bold text-navy text-sm mb-2">1. Who's coming?</p>
        <div className="flex flex-wrap gap-2">{kids.map((k) => (
          <button key={k.id} onClick={() => toggle(k.id)} className={`px-4 h-11 rounded-full border-2 font-bold text-sm transition-colors ${pick.includes(k.id) ? "bg-navy text-white border-navy" : "border-slate-200 text-navy"}`} data-testid={`event-kid-${k.id}`}>{k.first_name}</button>))}</div></div>
      <div><p className="font-bold text-navy text-sm mb-2">2. Choose your arrival time</p>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">{e.slots.map((s) => (
          <button key={s.value} onClick={() => setArrival(s.value)} className={`h-11 rounded-xl border-2 text-sm font-bold transition-colors ${arrival === s.value ? "bg-turquoise border-turquoise text-navy" : "border-slate-200 text-slate-600"}`} data-testid={`event-slot-${s.value}`}>{s.label}</button>))}</div></div>
      <div><p className="font-bold text-navy text-sm mb-2">3. How will you pay?</p>
        <div className="grid grid-cols-2 gap-2">{[["cash", "Cash when I arrive", Banknote], ["woi", "WOI QR code", QrCode]].map(([k, l, I]) => (
          <button key={k} onClick={() => setPay(k)} className={`h-14 rounded-2xl border-2 font-bold text-sm flex items-center justify-center gap-2 ${pay === k ? "bg-navy text-white border-navy" : "border-slate-200 text-navy"}`} data-testid={`event-pay-${k}`}><I className="w-5 h-5" />{l}</button>))}</div></div>
      <Button disabled={!pick.length || !arrival || busy} onClick={book} className="w-full h-14 rounded-full bg-[#FFB703] hover:bg-[#FFB703]/90 text-navy font-black text-base" data-testid="event-book-btn">
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : `BOOK NOW · ${money(e.price * (pick.length || 1))}`}</Button>
    </div>
  );
}

function MyBookings({ rows, onPay, onCancel }) {
  if (!rows.length) { return null; }
  return (
    <div className="mt-6 space-y-2" data-testid="event-my-bookings">
      <p className="font-display font-bold text-navy">Your bookings</p>
      {rows.map((b) => (
        <div key={b.id} className="bg-slate-50 rounded-2xl p-3 text-sm flex flex-wrap items-center justify-between gap-2" data-testid={`event-my-booking-${b.id}`}>
          <span><b>{b.child_names.join(", ")}</b> · arrive {b.arrival_label} · {money(b.amount)} · ref <span className="font-mono">{b.reference}</span></span>
          {b.order_status === "confirmed" ? <span className="text-emerald-600 font-bold flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />Paid</span>
            : b.order_status === "awaiting_confirmation" ? <span className="text-amber-600 font-bold">Payment being confirmed</span>
              : <span className="flex items-center gap-2">{b.booking_confirmed && <span className="text-emerald-600 font-bold text-xs" data-testid={`event-booking-confirmed-${b.id}`}>Booking confirmed · payment due</span>}<Button size="sm" onClick={() => onPay(b)} className="rounded-full bg-navy text-white" data-testid={`event-pay-now-${b.id}`}>Make payment</Button>
                <Button size="sm" variant="ghost" onClick={() => onCancel(b)} className="rounded-full text-slate-500" data-testid={`event-cancel-${b.id}`}>Cancel</Button></span>}
        </div>))}
    </div>
  );
}

export function EventCard({ e }) {
  const { user } = useAuth();
  const [mine, setMine] = useState([]);
  const [payOrder, setPayOrder] = useState(null);
  const load = useCallback(() => { if (user) { api.get("/events/my/bookings").then((r) => setMine(r.data.filter((b) => b.event_id === e.id))).catch(() => {}); } }, [user, e.id]);
  useEffect(() => { load(); }, [load]);
  const booked = (d, pay) => {
    toast.success(`Booked! ${d.child_names.join(", ")} · arrive ${d.arrival_label}`);
    announceBooking();
    load();
    setPayOrder({ payment_order_id: d.payment_order_id, amount: d.amount, reference: d.reference });
  };
  const cancel = async (b) => { try { await api.post(`/events/my/bookings/${b.id}/cancel`); toast.success("Booking cancelled"); load(); } catch (err) { toast.error(apiErr(err)); } };
  return (
    <article className="grid lg:grid-cols-2 gap-8 items-start" data-testid={`event-${e.id}`}>
      <img src={e.image} alt={`${e.title} flyer`} className="w-full rounded-3xl shadow-2xl border-4 border-white/10" data-testid="event-flyer" />
      <div className="bg-white text-navy rounded-3xl p-5 sm:p-7">
        <span className="text-xs font-black tracking-[0.25em] text-[#FF4D8D]">SPECIAL EVENT</span>
        <h2 className="font-display font-black text-3xl sm:text-4xl mt-1" data-testid="event-title">{e.title}</h2>
        <div className="mt-3 space-y-1 text-sm font-semibold">
          <p className="flex items-center gap-2" data-testid="event-date"><CalendarDays className="w-4 h-4 text-turquoise" />{niceDate(e.date)}</p>
          <p className="flex items-center gap-2" data-testid="event-hours"><Clock className="w-4 h-4 text-turquoise" />{e.start_label} – {e.end_label}</p>
          <p className="text-2xl font-display font-black mt-2" data-testid="event-price">{money(e.price)} <span className="text-sm font-semibold text-slate-500">per child</span></p>
        </div>
        <p className="text-slate-600 mt-3 text-sm sm:text-base">{e.summary}</p>
        <div className="flex flex-wrap gap-2 mt-3">{e.activities.map((a) => <span key={a} className="bg-turquoise/10 text-navy text-xs font-bold rounded-full px-3 py-1.5 flex items-center gap-1"><Sparkles className="w-3 h-3 text-[#FF4D8D]" />{a}</span>)}</div>
        {e.note && <p className="mt-4 text-sm bg-[#FFF4D6] rounded-2xl p-3" data-testid="event-note">{e.note}</p>}
        <div className="mt-5 border-t pt-5">
          {user ? <BookingForm e={e} onBooked={booked} /> : (
            <div className="space-y-2"><p className="text-sm text-slate-600">Booking is required. Log in or create a free parent account to book your arrival time.</p>
              <div className="flex gap-2"><Link to="/login"><Button className="rounded-full bg-navy text-white" data-testid="event-login-btn">Parent Login</Button></Link>
                <Link to="/register"><Button className="rounded-full bg-[#FFB703] text-navy font-bold" data-testid="event-register-btn">Register</Button></Link></div></div>)}
          <MyBookings rows={mine} onPay={(b) => setPayOrder({ payment_order_id: b.payment_order_id, amount: b.amount, reference: b.reference })} onCancel={cancel} />
        </div>
      </div>
      {payOrder && <PaymentDialog order={payOrder} open={!!payOrder} onClose={() => setPayOrder(null)} onDone={load} />}
    </article>
  );
}

export function DashboardEvents() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/events").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  return (
    <div className="space-y-8" data-testid="dashboard-events">
      <div><h1 className="font-display font-black text-3xl sm:text-4xl text-navy">Events</h1>
        <p className="text-slate-600 text-sm sm:text-base mt-1">Book special events like our Costume Party. Choose your Explorers and arrival time.</p></div>
      {rows === null ? <p className="text-slate-400">Loading…</p> : rows.length === 0 ? <p className="text-slate-500" data-testid="events-empty">No upcoming events right now — check back soon!</p>
        : rows.map((e) => <div key={e.id} className="bg-navy rounded-3xl p-3 sm:p-6"><EventCard e={e} /></div>)}
    </div>
  );
}

export default function Events() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/events").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  return (
    <div className="bg-navy min-h-screen text-white">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20" data-testid="events-page">
        <span className="text-turquoise font-bold tracking-[0.3em] text-sm">WHAT'S ON</span>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl mt-2">Events at Adventure Rock</h1>
        <p className="text-white/70 mt-3 max-w-2xl text-sm sm:text-base">Themed parties and special days for the whole family. Every event needs a booking, so grab your spot and arrival time below.</p>
        <div className="mt-10 space-y-14">
          {rows === null ? <p className="text-white/50">Loading…</p> : rows.length === 0 ? <p className="text-white/60" data-testid="events-empty">No upcoming events right now — check back soon!</p>
            : rows.map((e) => <EventCard key={e.id} e={e} />)}
        </div>
      </main>
      <Footer />
    </div>
  );
}
